import { z } from 'zod';
import { Dateish, PaginationQuerySchema } from './common';

/**
 * Ver como cliente (spec 020): sessão de depuração auditada.
 *
 * Em 2026-09-28 havia 274 logins de portal e o que cada um enxerga depende de
 * quatro coisas ao mesmo tempo (vínculo, mesa visível, o que é público no
 * ticket, perfil do tenant). Quando alguém dizia "não vejo o ticket X", a
 * equipe não tinha como conferir sem pedir a senha da pessoa.
 *
 * A API do ERP emite um token PRÓPRIO, aceito só por ela e vinculado a uma
 * linha de auditoria conferida a cada requisição. Só leitura por construção: o
 * portão global recusa toda escrita com 403 `impersonation_read_only`.
 */

/** `aud` do token de depuração — é por ela que o portão o reconhece */
export const IMPERSONATION_AUDIENCE = 'erp-impersonation';
/** prazo máximo de uma sessão (FR-003) */
export const IMPERSONATION_TTL_MINUTES = 60;
export const IMPERSONATION_REASON_MIN = 10;
export const IMPERSONATION_REASON_MAX = 500;
/** janela da lista em Configurações (FR-010) — as linhas nunca são apagadas */
export const IMPERSONATION_LIST_DAYS = 90;
/**
 * Chave do FRAGMENTO que leva o token à aba nova (FR-007): `/portal#impersonation=<token>`.
 * Fragmento não vai ao servidor em requisição nenhuma, nem no Referer.
 */
export const IMPERSONATION_FRAGMENT_KEY = 'impersonation';

/**
 * Conta como o Postgres conta (`char_length`): um emoji é UM caractere. O
 * `length` do JavaScript conta dois, e um motivo aceito aqui morreria no CHECK
 * da tabela com 500.
 */
export function reasonLength(text: string): number {
  return Array.from(text.trim()).length;
}

export const ImpersonationReasonSchema = z
  .string()
  .trim()
  .refine(
    (text) => {
      const n = reasonLength(text);
      return n >= IMPERSONATION_REASON_MIN && n <= IMPERSONATION_REASON_MAX;
    },
    { message: `o motivo precisa ter de ${IMPERSONATION_REASON_MIN} a ${IMPERSONATION_REASON_MAX} caracteres` }
  );

/** POST /users/:id/impersonation */
export const ImpersonationStartBodySchema = z.object({ reason: ImpersonationReasonSchema }).strict();
export type ImpersonationStartBody = z.infer<typeof ImpersonationStartBodySchema>;

/**
 * `ended` = alguém encerrou (a própria aba ou outro admin); `expired` = o prazo
 * venceu sem encerramento. Derivada, nunca gravada: a linha guarda só os fatos.
 */
export const IMPERSONATION_STATUSES = ['active', 'ended', 'expired'] as const;
export const ImpersonationStatusSchema = z.enum(IMPERSONATION_STATUSES);
export type ImpersonationStatus = z.infer<typeof ImpersonationStatusSchema>;

export function impersonationStatus(
  row: { endedAt: Date | string | null; expiresAt: Date | string },
  now: Date = new Date()
): ImpersonationStatus {
  if (row.endedAt !== null) return 'ended';
  return new Date(row.expiresAt).getTime() <= now.getTime() ? 'expired' : 'active';
}

const PersonRefSchema = z.object({ id: z.number().int(), name: z.string() });

/**
 * O que a aba de depuração mostra na faixa (GET /impersonation/current) — e o
 * que a emissão devolve junto do token. `clients` = nomes dos clientes a que o
 * alvo está vinculado NESTE tenant.
 */
export const ImpersonationCurrentSchema = z.object({
  sessionId: z.number().int(),
  reason: z.string(),
  startedAt: Dateish,
  expiresAt: Dateish,
  actor: PersonRefSchema,
  target: PersonRefSchema.extend({ email: z.string(), clients: z.array(z.string()) }),
});
export type ImpersonationCurrent = z.infer<typeof ImpersonationCurrentSchema>;

export const ImpersonationStartResponseSchema = z.object({
  token: z.string(),
  session: ImpersonationCurrentSchema,
});
export type ImpersonationStartResponse = z.infer<typeof ImpersonationStartResponseSchema>;

/** Linha de GET /impersonation/sessions (Configurações › Sessões de depuração). */
export const ImpersonationSessionSchema = z.object({
  id: z.number().int(),
  reason: z.string(),
  status: ImpersonationStatusSchema,
  startedAt: Dateish,
  expiresAt: Dateish,
  endedAt: Dateish.nullable(),
  actor: PersonRefSchema.extend({ email: z.string() }),
  target: PersonRefSchema.extend({ email: z.string(), clients: z.array(z.string()) }),
  endedBy: PersonRefSchema.nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  /** requisições feitas com o token, recusadas inclusive */
  requestCount: z.number().int(),
  lastRequestAt: Dateish.nullable(),
});
export type ImpersonationSession = z.infer<typeof ImpersonationSessionSchema>;

/** `q` casa nome ou e-mail de quem pediu OU de quem foi visto (filtro por pessoa) */
export const ImpersonationListQuerySchema = PaginationQuerySchema.extend({
  q: z.string().trim().max(120).optional(),
});
export type ImpersonationListQuery = z.infer<typeof ImpersonationListQuerySchema>;
